import {
  LoanPaymentSchedule,
  Maybe,
  Money,
  PayOverTimeBanner,
  PayOverTimeLoanDetailsResult,
  PayOverTimeLoanDetailsResultTypenames,
  SlTitledListComponent,
} from '@varo/graphql-schema-types';
import mapToTransactionSectionList from 'features/payOverTime/adapters/mapToTransactionSectionList';
import { useEffect, useMemo, useRef } from 'react';
import GetPayOverTimeLoanDetails from 'services/graphql/queries/PayOverTimeLoanDetails';
import { TransactionGroup } from 'shared/components/TransactionSectionList/TransactionSectionList';
import { RequestPolicy, useQuery } from 'urql';

export interface PayOverTimeLoanDetails {
  loanTitle?: Maybe<string>;
  amountDue?: Maybe<Money>;
  dueDate?: Maybe<string>;
  id?: Maybe<string>;
  banner?: Maybe<PayOverTimeBanner | null>;
  paymentSchedule?: Maybe<LoanPaymentSchedule | null>;
  isAutoPayEnabled?: Maybe<boolean>;
  showNeverMissPaymentSheet?: Maybe<boolean>;
  remainingBalance: Maybe<Money>;
  transactions?: Maybe<SlTitledListComponent[] | null>;
  transactionsMap: Maybe<TransactionGroup[] | []>;
}

const usePayOverTimeLoanDetails = (
  loanId: string,
  requestPolicy: RequestPolicy = 'cache-and-network',
  onShowNeverMissPaymentSheet?: () => void,
) => {
  const shownNeverMissPaymentSheetLoanId = useRef<string | null>(null);
  const [{ data, fetching, error }, refetch] = useQuery<{
    payOverTimeLoanDetails: PayOverTimeLoanDetailsResult;
  }>({
    query: GetPayOverTimeLoanDetails,
    variables: { loanId },
    requestPolicy,
  });

  const loanDetailsData =
    data?.payOverTimeLoanDetails?.__typename ===
    PayOverTimeLoanDetailsResultTypenames.PayOverTimeLoanDetailsSuccess
      ? data.payOverTimeLoanDetails
      : null;

  const loanDetailsError =
    data?.payOverTimeLoanDetails?.__typename ===
    PayOverTimeLoanDetailsResultTypenames.PayOverTimeLoanDetailsError
      ? data.payOverTimeLoanDetails
      : null;

  const combinedError = error || loanDetailsError;

  const resultsObj: PayOverTimeLoanDetails = useMemo(
    () => ({
      loanTitle: loanDetailsData?.loanName,
      amountDue: loanDetailsData?.amountDue,
      banner: loanDetailsData?.banner,
      dueDate: loanDetailsData?.dueDate,
      id: loanDetailsData?.id,
      isAutoPayEnabled: loanDetailsData?.isAutoPayEnabled,
      showNeverMissPaymentSheet: loanDetailsData?.showNeverMissPaymentSheet,
      paymentSchedule: loanDetailsData?.paymentSchedule,
      remainingBalance: loanDetailsData?.remainingBalance
        ? loanDetailsData.remainingBalance
        : null,
      transactions: loanDetailsData?.transactions,
      transactionsMap:
        loanDetailsData?.transactions?.map((curr) => {
          return mapToTransactionSectionList(curr, false);
        }) || [],
    }),
    [loanDetailsData],
  );

  useEffect(() => {
    if (
      !onShowNeverMissPaymentSheet ||
      fetching ||
      combinedError ||
      resultsObj.showNeverMissPaymentSheet !== true ||
      shownNeverMissPaymentSheetLoanId.current === loanId
    ) {
      return;
    }

    shownNeverMissPaymentSheetLoanId.current = loanId;
    onShowNeverMissPaymentSheet();
  }, [
    combinedError,
    fetching,
    loanId,
    onShowNeverMissPaymentSheet,
    resultsObj.showNeverMissPaymentSheet,
  ]);

  return {
    error: combinedError,
    resultsObj,
    fetching,
    refetch,
  };
};

export default usePayOverTimeLoanDetails;
